import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Image from '../AppImage';
import Button from './Button';

const Header = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [activeSection, setActiveSection] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const navigationItems = [
    { 
      id: 'who-are-we', 
      label: 'Who are we', 
      labelAr: 'من نحن', 
      labelTr: 'Biz kimiz',
      description: 'About Us',
      descriptionAr: 'من نحن',
      descriptionTr: 'Hakkımızda'
    },
    { 
      id: 'sponsors', 
      label: 'Sponsor', 
      labelAr: 'الرعاة', 
      labelTr: 'Sponsor',
      description: 'Our Sponsors',
      descriptionAr: 'رعاتنا',
      descriptionTr: 'Sponsorlarımız'
    },
    { 
      id: 'success-partners', 
      label: 'Success partners', 
      labelAr: 'شركاء النجاح', 
      labelTr: 'Başarı Ortakları',
      description: 'Partners',
      descriptionAr: 'الشركاء',
      descriptionTr: 'Ortaklar'
    }
  ];

  const memorialTitle = {
    ar: 'ذكرى تحرير سوريا',
    en: 'Anniversary of the liberation of Syria',
    tr: 'Suriye\'nin kurtuluş yıldönümü'
  };

  const memorialDate = {
    ar: '٨ ديسمبر ٢٠٢٤',
    en: 'December 8, 2024',
    tr: '8 Aralık 2024'
  };

  const ctaText = {
    ar: 'سجِّل الآن',
    en: 'Register Now',
    tr: 'Şimdi Kayıt Ol'
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'en';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'en';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      // Find active section based on scroll position
      const scrollPosition = window.scrollY + 200;
      const sections = navigationItems.map(item => ({
        id: item.id,
        element: document.getElementById(item.id),
        offset: document.getElementById(item.id)?.offsetTop || 0
      }));

      const currentSection = sections
        .filter(section => section.element && scrollPosition >= section.offset)
        .pop();

      if (currentSection && !isNavigating) {
        setActiveSection(currentSection.id);
      } else if (!currentSection && window.scrollY < 100 && !isNavigating) {
        // Reset to empty when at top
        setActiveSection('');
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isNavigating]);

  // Handle body scroll lock when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      // Store the current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      // Restore body scroll
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    }

    // Cleanup function
    return () => {
      if (!isNavigating) {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
      }
    };
  }, [isMobileMenuOpen, isNavigating]);

  const handleNavClick = (sectionId) => {
    // Set navigating flag to prevent scroll restoration
    setIsNavigating(true);
    
    // Immediately set the target section to prevent flickering
    setActiveSection(sectionId);
    
    // Close mobile menu first
    setIsMobileMenuOpen(false);
    
    // Wait for menu animation and body scroll unlock
    setTimeout(() => {
      if (sectionId === 'hero') {
        // Scroll to top for hero section
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const element = document.getElementById(sectionId);
        if (element) {
          // Calculate scroll position with offset to position title higher
          const headerHeight = window.innerWidth >= 1024 ? 80 : 64; // lg:h-20 (80px) or h-16 (64px)
          const additionalOffset = 40; // Extra space to show title higher
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - headerHeight - additionalOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
      // Reset navigating flag after navigation completes
      setTimeout(() => setIsNavigating(false), 1000);
    }, 100);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const getLabel = (item) => {
    switch (currentLanguage) {
      case 'ar': return item?.labelAr;
      case 'tr': return item?.labelTr;
      default: return item?.label;
    }
  };

  const getDescription = (item) => {
    switch (currentLanguage) {
      case 'ar': return item?.descriptionAr;
      case 'tr': return item?.descriptionTr;
      default: return item?.description;
    }
  };

  const isRTL = currentLanguage === 'ar';

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-scroll-navigation transition-all duration-300 ${
          isScrolled 
            ? 'bg-background/95 backdrop-blur-sm shadow-memorial border-b border-border' 
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <button
                onClick={() => handleNavClick('hero')}
                className="flex items-center focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg p-0"
              >
                <Image 
                  src="/assets/logos/2-01.png" 
                  alt="Anniversary of the liberation of Syria Logo" 
                  className="h-12 lg:h-16 w-auto object-contain"
                />
              </button>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-2">
              {navigationItems?.map((item) => (
                <button
                  key={item?.id}
                  onClick={() => handleNavClick(item?.id)}
                  className={`font-body text-sm px-4 py-2 rounded-md memorial-transition ${
                    activeSection === item?.id
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-text-secondary hover:text-primary hover:bg-muted'
                  }`}
                >
                  {getLabel(item)}
                </button>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center space-x-5">
              <Button
                variant="default"
                size="sm"
                onClick={() => handleNavClick('get-involved')}
                className="cta-button"
              >
                {ctaText?.[currentLanguage]}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md p-2 text-text-secondary hover:text-primary memorial-transition"
              aria-label="Toggle mobile menu"
            >
              <Icon 
                name={isMobileMenuOpen ? "X" : "Menu"} 
                size={24} 
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-modal lg:hidden">
          <div 
            className="fixed inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className={`fixed top-0 ${isRTL ? 'left-0' : 'right-0'} w-full max-w-sm h-full bg-background shadow-xl overflow-y-auto ${
            isRTL ? 'animate-slide-in-left' : 'animate-slide-in-right'
          }`}>
            <div className="flex items-center justify-between p-6 border-b border-border">
              <Image 
                src="/assets/logos/2-01.png" 
                alt="Anniversary of the liberation of Syria Logo" 
                className="h-12 w-auto object-contain"
              />
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md p-2 text-text-secondary hover:text-primary memorial-transition"
              >
                <Icon name="X" size={24} />
              </button>
            </div>
            
            <nav className="px-6 py-4 space-y-1">
              {navigationItems?.map((item) => (
                <button
                  key={item?.id}
                  onClick={() => handleNavClick(item?.id)}
                  className={`w-full ${isRTL ? 'text-right' : 'text-left'} px-4 py-4 rounded-lg transition-colors duration-250 min-h-[56px] ${
                    activeSection === item?.id
                      ? `bg-primary/10 text-primary ${isRTL ? 'border-r-4' : 'border-l-4'} border-primary` 
                      : 'text-text-secondary hover:text-primary hover:bg-muted'
                  }`}
                >
                  <div className="font-medium text-base">{getLabel(item)}</div>
                  <div className="text-sm text-text-secondary mt-1">{getDescription(item)}</div>
                </button>
              ))}
            </nav>
            
            <div className="px-6 py-4 border-t border-border mt-4">
              <Button
                variant="default"
                fullWidth
                onClick={() => handleNavClick('get-involved')}
                className="cta-button"
              >
                {ctaText?.[currentLanguage]}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
